import retailStatusTypes from '../../consts/retail/retailStatusTypes';
import {strCode} from '../../consts/strCode';
import {selectIsOnline} from '../../state/selectors/global';
import {selectIotDevice, selectIotDevicesList} from '../../state/selectors/iotDevices';
import {
    selectIsRetailDevicesLoading,
    selectRetailDeviceBySerialNumber,
    selectRetailDevices,
    selectRetailLocalDevices,
} from '../../state/selectors/retail';
import {removeDevice, setHolderLastChargerDusn} from '../../state/slice/iotDevices';
import {
    removeRetailDevice,
    removeRetailDeviceAuthToken,
    setIsRetailDevicesLoading,
    setRetailDeviceData,
    setRetailLocalDeviceData,
    updateRetailDeviceData,
} from '../../state/slice/retail';
import {dispatch, getState} from '../../state/store';
import damService from '../dam/damService';
import log from '../logger/log';
import notificationService from '../notificationService';
import uamClientService from '../uam/uamClientService';
import retailClientService from './retailClientService';
import retailMappingService from './retailMappingService';

const DISCONNECT_TIMEOUT = 60 * 60 * 1000; // 1 hour

let isRetailDataFetching = false;

export const getRetailData = async () => {
    if (!selectIsOnline(getState())) return;

    if (isRetailDataFetching) {
        log.debug('RETAIL: getRetailData already in progress');
        return;
    }

    isRetailDataFetching = true;

    try {
        await getRetailDevices();

        checkStatusForGetRetailAuthToken();

        await Promise.all([retailClientService.getUsersRewards(), retailClientService.getUsersBadges()]);
    } catch (e) {
        log.error('RETAIL: Failed to get retail data', e);
    } finally {
        isRetailDataFetching = false;

        if (selectIsRetailDevicesLoading(getState())) {
            dispatch(setIsRetailDevicesLoading(false));
        }
    }
};

const requested = new Set();

const checkStatusForGetRetailAuthToken = () => {
    const iot = Object.values(selectIotDevicesList(getState()) || {});
    const retail = Object.values(selectRetailLocalDevices(getState()) || {});

    const iotByDusn = new Map(iot.map((d) => [d?.dusn ?? d?.serialNumber, d?.connectionStatus]));

    retail.forEach((r) => {
        const dusn = r?.dusn;

        if (!dusn) return;

        const iotStatus = iotByDusn.get(dusn);
        const retailStatus = r?.onboardingStatus;

        if (!iotStatus || !retailStatus) return;

        const needToken =
            (iotStatus === retailStatusTypes.ONBOARDED && retailStatus === retailStatusTypes.DISCONNECTED) ||
            (retailStatus === retailStatusTypes.ONBOARDED && iotStatus === retailStatusTypes.DISCONNECTED);

        if (!needToken) return;

        const c = r?.codentify;

        if (!c || requested.has(c)) return;

        requested.add(c);
        addRetailLocalDevice(c)
            .catch((e) => log.error('RETAIL: failed to get retail token', e))
            .finally(() => requested.delete(c));
    });
};
const getRetailDataWithLoader = async () => {
    if (!selectIsOnline(getState())) {
        return notificationService.showToastNotification({messageStr: strCode.CHECK_NETWORK_CONNECTION_WARN});
    }

    dispatch(setIsRetailDevicesLoading(true));

    await getRetailData();
};

const addRetailLocalDevice = async (codentify) => {
    return await retailClientService.getRetailAuthToken(codentify);
};

const connectRetailDevice = (device) => {
    const deviceData = retailMappingService.deviceDataMapping(device);

    connectDevice(deviceData);

    const holder = deviceData?.holder;

    if (holder?.serialNumber) {
        connectDevice(holder);
    }

    const serialNumber = deviceData?.serialNumber;
    const existing = selectRetailDeviceBySerialNumber(serialNumber)(getState());

    if (existing && Object.keys(existing).length > 0) {
        dispatch(updateRetailDeviceData(serialNumber, deviceData));
    } else {
        dispatch(setRetailDeviceData(deviceData));
    }
};

const connectDevice = async (device) => {
    const serialNumber = device?.serialNumber;

    log.debug(`RETAIL service: start device ${serialNumber} connect`, serialNumber);

    if (selectIotDevice(serialNumber)(getState())) {
        return log.debug(`RETAIL service: device ${serialNumber} is connected before`, serialNumber);
    }

    if (!filterNotExpiredDisconnected(device)) {
        return log.debug(`RETAIL service: device ${serialNumber} is expired`, serialNumber);
    }

    damService.setInitialDeviceData(device);

    try {
        log.info(`RETAIL service: connect device ${serialNumber}`, serialNumber);
        await damService.setDeviceAssets(serialNumber);
        Promise.all([
            damService.getDeviceConnectionStatus(serialNumber),
            damService.getWebSocketService(serialNumber).initWebSocket({serialNumber}),
        ]);
        log.info(`RETAIL service: successfully connected device ${serialNumber}`, serialNumber);
    } catch (e) {
        log.warn(`RETAIL service: failed to connect device ${serialNumber}`, e, serialNumber);
    } finally {
        if (device?.chargerSerialNumber) {
            dispatch(setHolderLastChargerDusn(serialNumber, device?.chargerSerialNumber));
        }
    }
};

const getRetailDevices = async () => {
    try {
        log.debug(`RETAIL service: start get retail devices`);
        const devices = await uamClientService.getRetailDevices();
        const devicesInStore = Object.keys(selectRetailDevices(getState()));
        const devicesInResponse = devices.map(({dusn}) => dusn);
        const devicesToRemove = devicesInStore.filter((sn) => !devicesInResponse.includes(sn));

        devices.forEach(connectRetailDevice);
        devicesToRemove.forEach((sn) => deleteDevice(sn));
    } catch (e) {
        log.error(`RETAIL service: failed to get retail devices`, e);
    }
};

const redeemUserRewards = async (userId, rewards) => {
    try {
        await uamClientService.postRetailRewards(userId, rewards);
        log.info(`RETAIL service: Successfully redeem user rewards for user ${userId}`);
    } catch (e) {
        log.error(`RETAIL service: Failed redeem user rewards for user ${userId}`);
        throw e;
    }
};

const deleteDevice = (serialNumber) => {
    const device = selectIotDevice(serialNumber)(getState());

    damService.getWebSocketService(serialNumber).disconnectWebSocket(serialNumber);
    dispatch(removeDevice(serialNumber));
    dispatch(removeRetailDevice(serialNumber));
    dispatch(removeRetailDeviceAuthToken(device?.codentify));
};

const addDeviceByUserQrCode = async ({user_id, first_name, last_name, target_groups, device: codentify}) => {
    try {
        dispatch(setIsRetailDevicesLoading(true));

        const device = retailMappingService.deviceDataMapping({
            codentify,
            user: {user_id, first_name, last_name, target_groups},
            isLocalDevice: true,
            startTimestamp: Date.now(),
        });

        await addRetailLocalDevice(codentify);
        dispatch(setRetailLocalDeviceData(device));

        await getRetailDataWithLoader();
    } catch (e) {
        log.warn('RETAIL: User QR processing failed', e);

        throw e;
    } finally {
        dispatch(setIsRetailDevicesLoading(false));
    }
};

const addDeviceByCodentify = async (codentify) => {
    try {
        dispatch(setIsRetailDevicesLoading(true));

        await addRetailLocalDevice(codentify);

        await getRetailDataWithLoader();
    } catch (e) {
        log.warn('RETAIL: User codentify processing failed', e);

        throw e;
    } finally {
        dispatch(setIsRetailDevicesLoading(false));
    }
};

const filterNotExpiredDisconnected = ({lastConnection, onboardingStatus}) => {
    if (onboardingStatus !== retailStatusTypes.DISCONNECTED) return true;

    return Date.now() - lastConnection <= DISCONNECT_TIMEOUT;
};

export default {
    getRetailDevices,
    getRetailDataWithLoader,
    redeemUserRewards,
    addRetailLocalDevice,
    getRetailData,
    addDeviceByUserQrCode,
    addDeviceByCodentify,
    filterNotExpiredDisconnected,
};
